import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ViewChild, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';
import { PartsManagementAdvanceFilterComponent } from '../parts-management-advance-filter/parts-management-advance-filter.component';
import { PartsManagementBulkUploadComponent } from '../parts-management-bulk-upload/parts-management-bulk-upload.component';

import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { PartsManagementFilterForm } from '../../../form-model/parts-management-filter.form.model';
import { ReplaySubject, timer } from 'rxjs';
import { PartsManagementFilter } from '../../../models/parts-management-filter.model';
import { PartsManagementSearchTextForm } from '../../../form-model/parts-management-search-text.form.model';
import { environment } from '../../../../environments/environment';
import { PartsManagementService } from '../../../services/parts-management.service';
import { ToastrService } from '../../../services/toastr-service';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { DownloadFilterCriteria, toFilter } from '../../../models/download-filter.model';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';

type FilterValue = string | string[] | boolean;

@Component({
  selector: 'app-parts-management-filters',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    PartsManagementAdvanceFilterComponent,
    PartsManagementBulkUploadComponent,
    TagWithOverflowComponent,
  ],
  templateUrl: './parts-management-filters.component.html',
  styleUrl: './parts-management-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FiltersComponent implements OnInit, AfterViewInit {
  isPartAddEnabled = signal(this.authPolicies.canAddPartManagement && environment.Features.EnablePartsManagement);
  showAdvanceFilters: boolean = false;
  canUserCreatePM: boolean = false;
  readonly screenWidth = window.innerWidth;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    partsManagementFilterForm: FormGroup<PartsManagementFilterForm>;
    partsManagementSearchTextForm: FormGroup<PartsManagementSearchTextForm>;
  }>;

  savedFilter!: FormGroup<PartsManagementFilterForm>;
  taggedFilters: { key: string; value: FilterValue }[] = [];
  markCompleteApiCallStatus: APICallStatus = 'NOT_STARTED';
  @Output() markAsCompleteSuccess = new EventEmitter<boolean>();
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() isPartsManagementList: boolean = true;
  advancedFilterLoading = false;
  @ViewChild(PartsManagementAdvanceFilterComponent) advFilter!: PartsManagementAdvanceFilterComponent;
  isDownload: boolean = false;

  constructor(
    public authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly partsManagementService: PartsManagementService,
  ) {}

  ngOnInit(): void {
    if (this.authPolicies.canAddPartManagement) {
      this.canUserCreatePM = true;
    }
  }

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private setFilterFormValue() {
    const filterValue = this.partsManagementService.getPartsManagementFilterFormValue();
    if (filterValue) {
      this.filterFormGroup.controls.partsManagementFilterForm.setValue(filterValue);
      this.updateSelectedFiltersSection();
    }
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.controls.partsManagementFilterForm?.controls.applyPMFilter?.valueChanges.subscribe((value) => {
      if (value) {
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.partsManagementService.setPartsManagementFilterFormValue(
          this.filterFormGroup.controls.partsManagementFilterForm.getRawValue(),
        );
      } else {
        this.partsManagementService.setPartsManagementFilterFormValue(null);
      }
    });
    this.filterFormGroup.controls.partsManagementSearchTextForm.controls.appliedPMSearchTextFilter?.valueChanges.subscribe((_) => {
      this.updateSelectedFiltersSection();
    });
  }

  unselectPartNo(): (_: string) => void {
    return (partCode: string) => {
      const partCodeControl = this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList;
      const currentValues = this.taggedFilters.find((x) => x.key == 'Part Number')?.value as string[];
      const updatedValue = currentValues.filter((code) => code !== partCode);
      const key = this.taggedFilters.find((x) => x.key == 'Part Number');
      if (key) {
        key.value = updatedValue;
      }
      partCodeControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectDescription(): (_: string) => void {
    return (description: string) => {
      const descriptionControl = this.filterFormGroup.controls.partsManagementFilterForm.controls.description;
      const currentValues = this.taggedFilters.find((x) => x.key == 'Description')?.value as string[];
      const updatedValue = currentValues.filter((desc) => desc !== description);
      const key = this.taggedFilters.find((x) => x.key == 'Description');
      if (key) {
        key.value = updatedValue;
      }
      descriptionControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectSpecificFilter(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1, itemValue = ''): void {
    switch (taggedFilter.key) {
      case 'Part Number':
        this.unselectPartNumberItem(itemValue);
        break;
      case 'Part Group':
        this.unselectPartGroupItem(taggedFilter.value, itemNumber);
        break;
      case 'Part Manufacturer':
        this.unselectPartManufacturerItem(taggedFilter.value, itemNumber);
        break;
      case 'Description':
        this.unselectDescriptionItem(itemValue);
        break;
      case 'Active':
        this.filterFormGroup.controls.partsManagementFilterForm.controls.partActiveStatusesList.setValue(null);
        break;
      case 'Core':
        this.filterFormGroup.controls.partsManagementFilterForm.controls.isSerialTagNumberRequired.setValue(null);
        break;
      case 'Deduct Core Value':
        this.filterFormGroup.controls.partsManagementFilterForm.controls.deductCore.setValue(null);
        break;
    }
    this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  private unselectPartNumberItem(itemValue: string): void {
    const partNum = this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList.value as string[];
    const updatedPartNumbers = partNum.filter((num) => num !== itemValue);
    this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList.setValue(
      updatedPartNumbers.length ? updatedPartNumbers : null,
    );
  }

  private unselectPartGroupItem(value: FilterValue, itemNumber: number): void {
    if (Array.isArray(value) && itemNumber >= 0 && itemNumber < value.length) {
      const updatedPartGroup = value.filter((_: string, index: number) => index !== itemNumber);
      this.filterFormGroup.controls.partsManagementFilterForm.controls.partGroupCodeList.setValue(
        updatedPartGroup.length ? updatedPartGroup : null,
      );
    }
  }

  private unselectPartManufacturerItem(value: FilterValue, itemNumber: number): void {
    if (Array.isArray(value) && itemNumber >= 0 && itemNumber < value.length) {
      const updatedPartManufacturer = value.filter((_: string, index: number) => index !== itemNumber);
      this.filterFormGroup.controls.partsManagementFilterForm.controls.partManufacturerCodeList.setValue(
        updatedPartManufacturer.length ? updatedPartManufacturer : null,
      );
    }
  }

  private unselectDescriptionItem(itemValue: string): void {
    const desc = this.filterFormGroup.controls.partsManagementFilterForm.controls.description.value as string[];
    const updatedDescriptions = desc.filter((d) => d !== itemValue);
    this.filterFormGroup.controls.partsManagementFilterForm.controls.description.setValue(
      updatedDescriptions.length ? updatedDescriptions : null,
    );
  }

  showAdvanceFiltersModal() {
    this.advancedFilterLoading = true;
    timer(500).subscribe(() => {
      this.showAdvanceFilters = true;
      this.saveCurrentFilters();
    });
  }

  advancedFilterHasOpened() {
    this.advancedFilterLoading = false;
  }

  onDownload() {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.partsManagementService.downloadToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('parts-management'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.partsManagementFilterForm;

    const partCodes = form.controls.partCodesList?.value;
    if (partCodes && partCodes.length > 0) {
      filters.push(toFilter('part_code', partCodes));
    }

    const partGroups = form.controls.partGroupCodeList?.value;
    if (partGroups && partGroups.length > 0) {
      filters.push(toFilter('part_group_code', partGroups));
    }

    const manufacturers = form.controls.partManufacturerCodeList?.value;
    if (manufacturers && manufacturers.length > 0) {
      filters.push(toFilter('part_manufacturer_code', manufacturers));
    }

    const activeStatuses = form.controls.partActiveStatusesList?.value;
    if (activeStatuses && activeStatuses.length > 0) {
      filters.push(toFilter('is_active', activeStatuses));
    }

    const coreValues = form.controls.isSerialTagNumberRequired?.value;
    if (coreValues && coreValues.length > 0) {
      filters.push(toFilter('is_serial_tag_number_required', coreValues));
    }

    const deductCoreValues = form.controls.deductCore?.value;
    if (deductCoreValues && deductCoreValues.length > 0) {
      filters.push(toFilter('deduct_core', deductCoreValues));
    }

    return filters;
  }

  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.controls.partsManagementFilterForm.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  private saveCurrentFilters(): void {
    this.savedFilter = this.formBuilder.group({
      partCodesList: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList.value,
      ),
      partGroupCodeList: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.partsManagementFilterForm.controls.partGroupCodeList.value,
      ),
      partManufacturerCodeList: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.partsManagementFilterForm.controls.partManufacturerCodeList.value,
      ),
      description: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.partsManagementFilterForm.controls.description.value,
      ),
      partActiveStatusesList: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.partsManagementFilterForm.controls.partActiveStatusesList.value,
      ),
      isSerialTagNumberRequired: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.partsManagementFilterForm.controls.isSerialTagNumberRequired.value,
      ),
      deductCore: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.partsManagementFilterForm.controls.deductCore.value,
      ),
      applyPMFilter: this.formBuilder.control<boolean | null>(
        this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.value,
      ),
    });
  }

  updateSelectedFiltersSection() {
    this.markAsTagged('Part Number', this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList.value);
    this.markAsTagged('Part Group', this.filterFormGroup.controls.partsManagementFilterForm.controls.partGroupCodeList.value);
    this.markAsTagged('Part Manufacturer', this.filterFormGroup.controls.partsManagementFilterForm.controls.partManufacturerCodeList.value);
    this.markAsTagged('Description', this.filterFormGroup.controls.partsManagementFilterForm.controls.description.value);
    this.markAsTagged('Active', this.filterFormGroup.controls.partsManagementFilterForm.controls.partActiveStatusesList.value);
    this.markAsTagged('Core', this.filterFormGroup.controls.partsManagementFilterForm.controls.isSerialTagNumberRequired.value);
    this.markAsTagged('Deduct Core Value', this.filterFormGroup.controls.partsManagementFilterForm.controls.deductCore.value);
    this.updateStatusTag();
  }

  private updateStatusTag() {
    const filters: PartsManagementFilter = {};
    if (this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList.value) {
      filters.partCodesList = this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList.value;
    }
    if (this.filterFormGroup.controls.partsManagementFilterForm.controls.partGroupCodeList.value) {
      filters.partGroupCodeList = this.filterFormGroup.controls.partsManagementFilterForm.controls.partGroupCodeList.value;
    }
    if (this.filterFormGroup.controls.partsManagementFilterForm.controls.partManufacturerCodeList.value) {
      filters.partManufacturerCodeList = this.filterFormGroup.controls.partsManagementFilterForm.controls.partManufacturerCodeList.value;
    }
    if (this.filterFormGroup.controls.partsManagementFilterForm.controls.description.value) {
      filters.description = this.filterFormGroup.controls.partsManagementFilterForm.controls.description.value;
    }
    if (this.filterFormGroup.controls.partsManagementFilterForm.controls.partActiveStatusesList.value) {
      filters.partActiveStatusesList = this.filterFormGroup.controls.partsManagementFilterForm.controls.partActiveStatusesList.value;
    }
    if (this.filterFormGroup.controls.partsManagementFilterForm.controls.isSerialTagNumberRequired.value) {
      filters.isSerialTagNumberRequired = this.filterFormGroup.controls.partsManagementFilterForm.controls.isSerialTagNumberRequired.value;
    }
    if (this.filterFormGroup.controls.partsManagementFilterForm.controls.deductCore.value) {
      filters.deductCore = this.filterFormGroup.controls.partsManagementFilterForm.controls.deductCore.value;
    }
  }

  markAsTagged(key: string, newValue: FilterValue | null) {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== false && newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string) {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string) {
    return getSharedFilterValue(this.taggedFilters, key);
  }

  getTaggedFilterValue(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1): FilterValue {
    switch (taggedFilter.key) {
      case 'Part Group': {
        let partGroupCodeList = taggedFilter.value as string[];
        return itemNumber >= 0 && itemNumber < partGroupCodeList.length ? partGroupCodeList[itemNumber] : partGroupCodeList;
      }
      case 'Part Manufacturer': {
        let partManufacturerCodeList = taggedFilter.value as string[];
        return itemNumber >= 0 && itemNumber < partManufacturerCodeList.length
          ? partManufacturerCodeList[itemNumber]
          : partManufacturerCodeList;
      }
      case 'Active': {
        let activeStatusesList = taggedFilter.value as string[];
        return activeStatusesList;
      }
      case 'Core': {
        let isSerialTagNumberRequiredList = taggedFilter.value as string[];
        return isSerialTagNumberRequiredList;
      }
      case 'Deduct Core Value': {
        let deductCoreList = taggedFilter.value as string[];
        return deductCoreList;
      }
      default:
        return '';
    }
  }

  getTaggedPartNo(): string[] {
    return (this.taggedFilters.find((taggedFilter) => taggedFilter.key == 'Part Number')?.value as string[]) || [];
  }

  getTaggedDescription(): string[] {
    return (this.taggedFilters.find((taggedFilter) => taggedFilter.key == 'Description')?.value as string[]) || [];
  }

  getTaggedPartGroup(): string[] {
    const partGroup = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Part Group');
    return partGroup && Array.isArray(partGroup.value) ? partGroup.value : [];
  }

  getTaggedManufacturerCode(): string[] {
    const partManufacturer = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Part Manufacturer');
    return partManufacturer && Array.isArray(partManufacturer.value) ? partManufacturer.value : [];
  }

  getTaggedActive(): string[] {
    const active = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Active');
    return active && Array.isArray(active.value) ? active.value : [];
  }

  getTaggedCore(): string[] {
    const core = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Core');
    return core && Array.isArray(core.value) ? core.value : [];
  }

  getTaggedDeductCore(): string[] {
    const deductCore = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Deduct Core Value');
    return deductCore && Array.isArray(deductCore.value) ? deductCore.value : [];
  }

  unselectSpecificActiveFilter(value: string): void {
    const control = this.filterFormGroup.controls.partsManagementFilterForm.controls.partActiveStatusesList;
    const currentValues = (control.value as string[]) || [];
    const updatedValues = currentValues.filter((v: string) => v !== value);
    control.setValue(updatedValues.length ? updatedValues : null);
    this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  unselectSpecificCoreFilter(value: string): void {
    const control = this.filterFormGroup.controls.partsManagementFilterForm.controls.isSerialTagNumberRequired;
    const currentValues = (control.value as string[]) || [];
    const updatedValues = currentValues.filter((v: string) => v !== value);
    control.setValue(updatedValues.length ? updatedValues : null);
    this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  unselectSpecificDeductCoreFilter(value: string): void {
    const control = this.filterFormGroup.controls.partsManagementFilterForm.controls.deductCore;
    const currentValues = (control.value as string[]) || [];
    const updatedValues = currentValues.filter((v: string) => v !== value);
    control.setValue(updatedValues.length ? updatedValues : null);
    this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }
}
